import React, { useState } from "react";
import styled from "@emotion/styled";
import MenuItem from "@mui/material/MenuItem";
import { Modal } from "../../../components/Modal";
import { MUISelect } from "../../../components/MUISelect";
import { Button } from "../../../UI/MUIButton";
import { useModalContext } from "../../../contexts/ModalContext/context";
import useAuth from "../../login/AuthContext";
import { useSnackbar } from "../../../hooks/useSnackbar";
import { Box } from "../../../UI/Box";
import { post, BASE_URL_V1 } from "../../../services/Common";

const INTEGRATION_TYPES = [
  "Entrata",
  "ACCSFApi",
  "TenantKey",
  "Email",
  "Kinetic"
];

const ModalContent = styled(Box)`
  padding: 32px 24px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const ButtonContainer = styled(Box)`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
`;

const StyledButton = styled(Button)`
  min-width: 120px;
  padding: 10px 24px;
  text-transform: none;
  font-weight: 500;
  font-size: 14px;
  border-radius: 4px;
`;

const SaveButton = styled(StyledButton)`
  background-color: #5E4DB2;
  color: white;

  &:hover {
    background-color: #4A3D8F;
  }

  &:disabled {
    background-color: #9E9E9E;
    color: white;
  }
`;

const CancelButton = styled(StyledButton)`
  border: 1px solid #BDBDBD;
  color: #424242;
  background-color: white;

  &:hover {
    background-color: #F5F5F5;
    border-color: #9E9E9E;
  }
`;

interface CreateIntegrationModalProps {
  portfolioId: string;
  onSuccess: () => void;
}

export const CreateIntegrationModal: React.FC<CreateIntegrationModalProps> = ({
  portfolioId,
  onSuccess,
}) => {
  const { setIsOpen } = useModalContext();
  const { getAuthToken } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [selectedType, setSelectedType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
    setSelectedType("");
  };

  const handleSave = async () => {
    if (!selectedType) {
      showErrorSnackbar("Please select a connection type");
      return;
    }

    try {
      setIsLoading(true);
      const authToken = await getAuthToken();
      const url = `${BASE_URL_V1}/import/settings/portfolio/${portfolioId}/importType/${selectedType}`;

      const response = await post(url, authToken, undefined, {enabled: true});

      if (response.ok) {
        showSuccessSnackbar("Integration created successfully");
        handleClose();
        onSuccess();
      } else {
        showErrorSnackbar("Failed to create integration");
      }
    } catch (error) {
      showErrorSnackbar("Failed to create integration");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal title="Create Integration" onClose={handleClose} maxWidth="sm" fullWidth>
      <ModalContent>
        <Box>
          <MUISelect
            id="connection-type-select"
            label="Connection Type"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as string)}
            fullWidth
            variant="outlined"
          >
            {INTEGRATION_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </MUISelect>
        </Box>

        <ButtonContainer>
          <SaveButton onClick={handleSave} disabled={isLoading || !selectedType}>
            {isLoading ? "Saving..." : "Save"}
          </SaveButton>
          <CancelButton onClick={handleClose} disabled={isLoading}>
            Cancel
          </CancelButton>
        </ButtonContainer>
      </ModalContent>
    </Modal>
  );
};

